import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Loader2, Lock, MessageCircle, Sparkles } from 'lucide-react';
import { useClientAuth } from '../../context/ClientAuthContext';

const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleString('fr-CH', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};

export const MessagingPanel = ({ en }) => {
  const { authFetch } = useClientAuth();
  const api = useCallback((p, o) => authFetch(p, o).then((r) => (r.ok ? r.json() : Promise.reject(r))), [authFetch]);
  const [data, setData] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const nav = useNavigate();

  const load = useCallback(() => {
    api('/api/tracking/messages').then(setData).catch(() => setData({ messages: [], transformation: false }));
  }, [api]);

  useEffect(() => {
    load();
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [data]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api('/api/tracking/messages', { method: 'POST', body: JSON.stringify({ text: text.trim() }) });
      setText('');
      load();
    } catch { /* ignore */ } finally { setSending(false); }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  if (!data.transformation) {
    return (
      <div className="rounded-3xl border border-purple-500/30 bg-gradient-to-br from-purple-500/10 to-black p-10 text-center max-w-lg mx-auto" data-testid="messaging-locked">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500/25 to-primary/20 flex items-center justify-center mx-auto mb-4">
          <Lock className="w-7 h-7 text-purple-300" />
        </div>
        <h3 className="text-xl font-bold text-white">{en ? 'Messaging is reserved for the Transformation pack' : 'La messagerie est réservée au pack Transformation'}</h3>
        <p className="text-zinc-400 mt-2 text-sm">{en ? 'Chat 24/7 with coach Laurent Waelti, get personalized training programs, monthly video calls and more.' : 'Échangez 24/7 avec le coach Laurent Waelti, recevez des programmes de musculation personnalisés, une visio mensuelle et bien plus.'}</p>
        <button onClick={() => nav('/transformation')} className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-purple-500 to-primary hover:scale-[1.02] transition-transform" data-testid="messaging-upsell-btn">
          <Sparkles className="w-4 h-4" /> {en ? 'Discover the Transformation pack' : 'Découvrir le pack Transformation'}
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] overflow-hidden max-w-2xl mx-auto" data-testid="messaging-panel">
      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-white/10 bg-gradient-to-r from-purple-500/10 to-transparent">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-primary flex items-center justify-center"><MessageCircle className="w-4.5 h-4.5 text-white" /></div>
        <div>
          <p className="font-bold text-white text-sm">{en ? 'Coach Laurent · Body-Up' : 'Coach Laurent · Body-Up'}</p>
          <p className="text-[11px] text-emerald-400 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> {en ? '24/7 support' : 'Support 24/7'}</p>
        </div>
      </div>

      <div className="h-[55vh] overflow-y-auto p-4 flex flex-col gap-2.5" data-testid="messages-list">
        {data.messages.length === 0 ? (
          <p className="text-zinc-500 text-sm text-center my-auto">{en ? 'Start the conversation with your coach 👋' : 'Démarrez la conversation avec votre coach 👋'}</p>
        ) : data.messages.map((m) => (
          <div key={m.message_id} className={`max-w-[80%] ${m.sender === 'client' ? 'self-end items-end' : 'self-start items-start'} flex flex-col`} data-testid="message-bubble">
            <div className={`rounded-2xl px-4 py-2.5 text-sm ${m.sender === 'client' ? 'bg-gradient-to-r from-primary to-pink-500 text-white rounded-br-sm' : 'bg-zinc-800 text-zinc-100 border border-white/10 rounded-bl-sm'}`}>
              {m.text}
            </div>
            <span className="text-[10px] text-zinc-600 mt-1 px-1">{m.sender === 'coach' ? 'Coach · ' : ''}{fmtTime(m.created_at)}</span>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div className="flex gap-2 p-3 border-t border-white/10">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
          placeholder={en ? 'Write a message…' : 'Écrivez un message…'}
          className="flex-1 bg-zinc-800 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-primary focus:outline-none" data-testid="message-input" />
        <button onClick={send} disabled={sending || !text.trim()} className="px-4 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-50 flex items-center" data-testid="send-message-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

export default MessagingPanel;
